import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { meQueryKey } from '../api/me';
import type { Child, Direction, IntakeResponse } from '../api/types';

/** Запросы первого входа. */

export function useDirections() {
  return useQuery({
    queryKey: ['directions'],
    queryFn: () => api.get<Direction[]>('/api/directions'),
    staleTime: 5 * 60_000,
  });
}

const intakeQuery = (directionId: number) => ({
  queryKey: ['intake', directionId] as const,
  queryFn: () => api.get<IntakeResponse>('/api/intake', { directionId }),
  staleTime: 60_000,
});

export function useIntake(directionId: number | null) {
  return useQuery({ ...intakeQuery(directionId ?? 0), enabled: !!directionId });
}

/**
 * Первичный приём по каждому направлению сразу: в списке направлений нужны цена,
 * длительность и число принимающих специалистов — ровно то, что отдаёт
 * `/api/intake`. Направлений у центра единицы, так что это дешевле, чем кажется.
 */
export function useIntakeByDirection(directionIds: number[]) {
  const results = useQueries({ queries: directionIds.map((id) => intakeQuery(id)) });
  const byId = new Map<number, IntakeResponse>();
  directionIds.forEach((id, i) => {
    const data = results[i]?.data;
    if (data) byId.set(id, data);
  });
  return { byId, isPending: results.some((r) => r.isPending) };
}

export function useCreateChild() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; birthYear?: number | null; note?: string }) =>
      api.post<Child>('/api/me/children', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQueryKey }),
  });
}

/** Заявка уходит администратору всегда, выбранному специалисту — дополнительно. */
export function useCreateReferral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { childId: number; directionId?: number | null; preferredSpecialistId?: number | null; note?: string }) =>
      api.post<unknown>('/api/me/referrals', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQueryKey }),
  });
}
